import { BaseApiMethodExecutor } from '../../api/BaseApiMethodExecutor/BaseApiMethodExecutor';
import { ApiMethodExecutingError } from '../../service/Errors';

import {
    VideoCreatorApiBaseResponse,
    VideoCreatorApiResponse,
    VideoCreatorApiVideoData,
    VideoCreatorImageUploadResponse,
    VideoCreatorTemplateListDataResponse
} from './typings';

const VideoCreatorHeaders = {
    Accept: 'application/json, text/javascript, */*; q=0.01',
    'Accept-encoding': 'gzip, deflate',
    Connection: 'keep-alive'
};

export class VideoCreatorApiMethodExecutor extends BaseApiMethodExecutor {
    private apiPath: string;

    constructor(apiPath?: string) {
        super(apiPath || '');

        this.apiPath = apiPath ? apiPath : `/canvas-api/video/constructor-files`;
    }

    protected get defaultHeaders(): HeadersInit {
        return { ...this._defaultHeaders, ...VideoCreatorHeaders };
    }

    protected get headersWithoutContentType(): HeadersInit {
        const headers = { ...this.defaultHeaders };
        delete headers['Content-Type'];

        return headers;
    }

    async getUserVideoData(userId: string, videoId: string): Promise<VideoCreatorApiResponse> {
        return this.createRequest<VideoCreatorApiResponse>(this.apiPath, { method: 'GET' }, [
            `id=${videoId}`,
            `user_id=${userId}`
        ]);
    }

    async postUserVideoData(userId: string, apiVideoData: VideoCreatorApiVideoData): Promise<VideoCreatorApiResponse> {
        return await this.createRequest<VideoCreatorApiResponse>(
            this.apiPath,
            {
                method: 'POST',
                body: JSON.stringify(apiVideoData)
            },
            [`user_id=${userId}`]
        );
    }

    async getTemplateListData(userId: string): Promise<VideoCreatorTemplateListDataResponse> {
        return this.createRequest<VideoCreatorTemplateListDataResponse>(`${this.apiPath}/my`, { method: 'GET' }, [
            `user_id=${userId}`
        ]);
    }

    async uploadImage(clientId: string, image: File): Promise<VideoCreatorImageUploadResponse> {
        const formData = new FormData();

        formData.append('attachment', image, image.name);
        const params = {
            method: 'POST',
            body: formData,
            headers: this.headersWithoutContentType
        };

        return this.createRequest<VideoCreatorImageUploadResponse>(`/canvas-api/files`, params, [
            `client_id=${clientId}`
        ]);
    }

    async uploadImageByLink(userId: string, fileUrl: string): Promise<VideoCreatorTemplateListDataResponse> {
        return this.createRequest<VideoCreatorTemplateListDataResponse>(
            `/canvas-api/files/url`,
            {
                method: 'POST',
                body: JSON.stringify({ fileUrl })
            },
            [`client_id=${userId}`]
        );
    }

    async createRequest<T extends VideoCreatorApiBaseResponse>(
        path: string,
        params: RequestInit = {},
        queryParams?: string[]
    ): Promise<T> {
        const query = queryParams || [];
        const newPath = `${path}${query.length > 0 ? `?${query.join('&')}` : ''}`;

        params.headers = params.headers || this.defaultHeaders;

        const data = await super.createRequest<T>(newPath, params);

        if (data.error && data.message) {
            throw new ApiMethodExecutingError({ message: data.message });
        }

        return data;
    }
}
